/**
 * Normalizes a phone number string to E.164 format (+[country code][digits]).
 *
 * Rules:
 *  - If it already starts with '+', strip non-digits and reattach the '+'.
 *  - No '+': prepend the provided defaultDialCode digits + the number's digits.
 *  - Special-case: 10 digits with defaultDialCode '+1' → +1XXXXXXXXXX (NANP).
 *  - Special-case: 11 digits starting with '1' with defaultDialCode '+1' → +1XXXXXXXXXX.
 *  - Empty / null / whitespace-only: returned unchanged.
 */
export function normalizePhoneE164(phone: string, defaultDialCode: string = '+1'): string {
  if (!phone || !phone.trim()) return phone;

  const hasPlus = phone.replace(/^\s+/, '').charAt(0) === '+';
  const digits = phone.replace(/\D/g, '');

  if (!digits) return phone;

  if (hasPlus) {
    return '+' + digits;
  }

  const dcDigits = defaultDialCode.replace(/\D/g, '');

  // NANP special cases (US/CA +1)
  if (dcDigits === '1') {
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
  }

  return '+' + dcDigits + digits;
}

export interface DialCodeOption {
  isoCode: string;
  dialCode: string;
  name: string;
  pinned?: boolean;
}

/** Maps ISO 3166-1 alpha-2 country codes to their ITU calling code (e.g. 'GB' → '+44'). */
export const COUNTRY_DIAL_CODES: Record<string, string> = {
  AF: '+93',  AX: '+358', AL: '+355', DZ: '+213', AS: '+1684', AD: '+376',
  AO: '+244', AI: '+1264', AQ: '+672', AG: '+1268', AR: '+54',  AM: '+374',
  AW: '+297', AU: '+61',  AT: '+43',  AZ: '+994', BS: '+1242', BH: '+973',
  BD: '+880', BB: '+1246', BY: '+375', BE: '+32',  BZ: '+501', BJ: '+229',
  BM: '+1441', BT: '+975', BO: '+591', BQ: '+599', BA: '+387', BW: '+267',
  BV: '+47',  BR: '+55',  IO: '+246', BN: '+673', BG: '+359', BF: '+226',
  BI: '+257', KH: '+855', CM: '+237', CA: '+1',   CV: '+238', KY: '+1345',
  CF: '+236', TD: '+235', CL: '+56',  CN: '+86',  CX: '+61',  CC: '+61',
  CO: '+57',  KM: '+269', CG: '+242', CD: '+243', CK: '+682', CR: '+506',
  CI: '+225', HR: '+385', CU: '+53',  CW: '+599', CY: '+357', CZ: '+420',
  DK: '+45',  DJ: '+253', DM: '+1767', DO: '+1809', EC: '+593', EG: '+20',
  SV: '+503', GQ: '+240', ER: '+291', EE: '+372', ET: '+251', FK: '+500',
  FO: '+298', FJ: '+679', FI: '+358', FR: '+33',  GF: '+594', PF: '+689',
  TF: '+262', GA: '+241', GM: '+220', GE: '+995', DE: '+49',  GH: '+233',
  GI: '+350', GR: '+30',  GL: '+299', GD: '+1473', GP: '+590', GU: '+1671',
  GT: '+502', GG: '+44',  GN: '+224', GW: '+245', GY: '+592', HT: '+509',
  HM: '+61',  HN: '+504', HK: '+852', HU: '+36',  IS: '+354', IN: '+91',
  ID: '+62',  IR: '+98',  IQ: '+964', IE: '+353', IM: '+44',  IL: '+972',
  IT: '+39',  JM: '+1876', JP: '+81', JE: '+44',  JO: '+962', KZ: '+7',
  KE: '+254', KI: '+686', KP: '+850', KR: '+82',  KW: '+965', KG: '+996',
  LA: '+856', LV: '+371', LB: '+961', LS: '+266', LR: '+231', LY: '+218',
  LI: '+423', LT: '+370', LU: '+352', MO: '+853', MK: '+389', MG: '+261',
  MW: '+265', MY: '+60',  MV: '+960', ML: '+223', MT: '+356', MH: '+692',
  MQ: '+596', MR: '+222', MU: '+230', YT: '+262', MX: '+52',  FM: '+691',
  MD: '+373', MC: '+377', MN: '+976', ME: '+382', MS: '+1664', MA: '+212',
  MZ: '+258', MM: '+95',  NA: '+264', NR: '+674', NP: '+977', NL: '+31',
  NC: '+687', NZ: '+64',  NI: '+505', NE: '+227', NG: '+234', NU: '+683',
  NF: '+672', MP: '+1670', NO: '+47', OM: '+968', PK: '+92',  PW: '+680',
  PS: '+970', PA: '+507', PG: '+675', PY: '+595', PE: '+51',  PH: '+63',
  PN: '+64',  PL: '+48',  PT: '+351', PR: '+1787', QA: '+974', RE: '+262',
  RO: '+40',  RU: '+7',   RW: '+250', BL: '+590', SH: '+290', KN: '+1869',
  LC: '+1758', MF: '+590', PM: '+508', VC: '+1784', WS: '+685', SM: '+378',
  ST: '+239', SA: '+966', SN: '+221', RS: '+381', SC: '+248', SL: '+232',
  SG: '+65',  SX: '+1721', SK: '+421', SI: '+386', SB: '+677', SO: '+252',
  ZA: '+27',  GS: '+500', SS: '+211', ES: '+34',  LK: '+94',  SD: '+249',
  SR: '+597', SJ: '+47',  SZ: '+268', SE: '+46',  CH: '+41',  SY: '+963',
  TW: '+886', TJ: '+992', TZ: '+255', TH: '+66',  TL: '+670', TG: '+228',
  TK: '+690', TO: '+676', TT: '+1868', TN: '+216', TR: '+90', TM: '+993',
  TC: '+1649', TV: '+688', UG: '+256', UA: '+380', AE: '+971', GB: '+44',
  UM: '+1',   US: '+1',   UY: '+598', UZ: '+998', VU: '+678', VA: '+39',
  VE: '+58',  VN: '+84',  VG: '+1284', VI: '+1340', WF: '+681', EH: '+212',
  YE: '+967', ZM: '+260', ZW: '+263',
};

export function getDialCodeForCountry(countryCode: string): string {
  return COUNTRY_DIAL_CODES[countryCode] || '+1';
}

/**
 * Ordered list for the dial-code picker.
 * Common countries appear first (pinned: true); all others follow alphabetically.
 */
export const DIAL_CODE_OPTIONS: DialCodeOption[] = [
  { isoCode: 'US', dialCode: '+1',   name: 'United States',        pinned: true },
  { isoCode: 'CA', dialCode: '+1',   name: 'Canada',               pinned: true },
  { isoCode: 'GB', dialCode: '+44',  name: 'United Kingdom',       pinned: true },
  { isoCode: 'AU', dialCode: '+61',  name: 'Australia',            pinned: true },
  { isoCode: 'NZ', dialCode: '+64',  name: 'New Zealand',          pinned: true },
  { isoCode: 'FR', dialCode: '+33',  name: 'France',               pinned: true },
  { isoCode: 'DE', dialCode: '+49',  name: 'Germany',              pinned: true },
  { isoCode: 'IE', dialCode: '+353', name: 'Ireland',              pinned: true },
  { isoCode: 'MX', dialCode: '+52',  name: 'Mexico',               pinned: true },
  // All others alphabetically (pinned countries excluded)
  { isoCode: 'AF', dialCode: '+93',   name: 'Afghanistan' },
  { isoCode: 'AX', dialCode: '+358',  name: 'Åland' },
  { isoCode: 'AL', dialCode: '+355',  name: 'Albania' },
  { isoCode: 'DZ', dialCode: '+213',  name: 'Algeria' },
  { isoCode: 'AS', dialCode: '+1684', name: 'American Samoa' },
  { isoCode: 'AD', dialCode: '+376',  name: 'Andorra' },
  { isoCode: 'AO', dialCode: '+244',  name: 'Angola' },
  { isoCode: 'AI', dialCode: '+1264', name: 'Anguilla' },
  { isoCode: 'AQ', dialCode: '+672',  name: 'Antarctica' },
  { isoCode: 'AG', dialCode: '+1268', name: 'Antigua and Barbuda' },
  { isoCode: 'AR', dialCode: '+54',   name: 'Argentina' },
  { isoCode: 'AM', dialCode: '+374',  name: 'Armenia' },
  { isoCode: 'AW', dialCode: '+297',  name: 'Aruba' },
  { isoCode: 'AT', dialCode: '+43',   name: 'Austria' },
  { isoCode: 'AZ', dialCode: '+994',  name: 'Azerbaijan' },
  { isoCode: 'BS', dialCode: '+1242', name: 'Bahamas' },
  { isoCode: 'BH', dialCode: '+973',  name: 'Bahrain' },
  { isoCode: 'BD', dialCode: '+880',  name: 'Bangladesh' },
  { isoCode: 'BB', dialCode: '+1246', name: 'Barbados' },
  { isoCode: 'BY', dialCode: '+375',  name: 'Belarus' },
  { isoCode: 'BE', dialCode: '+32',   name: 'Belgium' },
  { isoCode: 'BZ', dialCode: '+501',  name: 'Belize' },
  { isoCode: 'BJ', dialCode: '+229',  name: 'Benin' },
  { isoCode: 'BM', dialCode: '+1441', name: 'Bermuda' },
  { isoCode: 'BT', dialCode: '+975',  name: 'Bhutan' },
  { isoCode: 'BO', dialCode: '+591',  name: 'Bolivia' },
  { isoCode: 'BQ', dialCode: '+599',  name: 'Bonaire, Sint Eustatius and Saba' },
  { isoCode: 'BA', dialCode: '+387',  name: 'Bosnia and Herzegovina' },
  { isoCode: 'BW', dialCode: '+267',  name: 'Botswana' },
  { isoCode: 'BV', dialCode: '+47',   name: 'Bouvet Island' },
  { isoCode: 'BR', dialCode: '+55',   name: 'Brazil' },
  { isoCode: 'IO', dialCode: '+246',  name: 'British Indian Ocean Territory' },
  { isoCode: 'BN', dialCode: '+673',  name: 'Brunei Darussalam' },
  { isoCode: 'BG', dialCode: '+359',  name: 'Bulgaria' },
  { isoCode: 'BF', dialCode: '+226',  name: 'Burkina Faso' },
  { isoCode: 'BI', dialCode: '+257',  name: 'Burundi' },
  { isoCode: 'KH', dialCode: '+855',  name: 'Cambodia' },
  { isoCode: 'CM', dialCode: '+237',  name: 'Cameroon' },
  { isoCode: 'CV', dialCode: '+238',  name: 'Cape Verde' },
  { isoCode: 'KY', dialCode: '+1345', name: 'Cayman Islands' },
  { isoCode: 'CF', dialCode: '+236',  name: 'Central African Republic' },
  { isoCode: 'TD', dialCode: '+235',  name: 'Chad' },
  { isoCode: 'CL', dialCode: '+56',   name: 'Chile' },
  { isoCode: 'CN', dialCode: '+86',   name: 'China' },
  { isoCode: 'CX', dialCode: '+61',   name: 'Christmas Island' },
  { isoCode: 'CC', dialCode: '+61',   name: 'Cocos (Keeling) Islands' },
  { isoCode: 'CO', dialCode: '+57',   name: 'Colombia' },
  { isoCode: 'KM', dialCode: '+269',  name: 'Comoros' },
  { isoCode: 'CG', dialCode: '+242',  name: 'Congo (Brazzaville)' },
  { isoCode: 'CD', dialCode: '+243',  name: 'Congo (Kinshasa)' },
  { isoCode: 'CK', dialCode: '+682',  name: 'Cook Islands' },
  { isoCode: 'CR', dialCode: '+506',  name: 'Costa Rica' },
  { isoCode: 'CI', dialCode: '+225',  name: "Côte d'Ivoire" },
  { isoCode: 'HR', dialCode: '+385',  name: 'Croatia' },
  { isoCode: 'CU', dialCode: '+53',   name: 'Cuba' },
  { isoCode: 'CW', dialCode: '+599',  name: 'Curaçao' },
  { isoCode: 'CY', dialCode: '+357',  name: 'Cyprus' },
  { isoCode: 'CZ', dialCode: '+420',  name: 'Czech Republic' },
  { isoCode: 'DK', dialCode: '+45',   name: 'Denmark' },
  { isoCode: 'DJ', dialCode: '+253',  name: 'Djibouti' },
  { isoCode: 'DM', dialCode: '+1767', name: 'Dominica' },
  { isoCode: 'DO', dialCode: '+1809', name: 'Dominican Republic' },
  { isoCode: 'EC', dialCode: '+593',  name: 'Ecuador' },
  { isoCode: 'EG', dialCode: '+20',   name: 'Egypt' },
  { isoCode: 'SV', dialCode: '+503',  name: 'El Salvador' },
  { isoCode: 'GQ', dialCode: '+240',  name: 'Equatorial Guinea' },
  { isoCode: 'ER', dialCode: '+291',  name: 'Eritrea' },
  { isoCode: 'EE', dialCode: '+372',  name: 'Estonia' },
  { isoCode: 'ET', dialCode: '+251',  name: 'Ethiopia' },
  { isoCode: 'FK', dialCode: '+500',  name: 'Falkland Islands' },
  { isoCode: 'FO', dialCode: '+298',  name: 'Faroe Islands' },
  { isoCode: 'FJ', dialCode: '+679',  name: 'Fiji' },
  { isoCode: 'FI', dialCode: '+358',  name: 'Finland' },
  { isoCode: 'GF', dialCode: '+594',  name: 'French Guiana' },
  { isoCode: 'PF', dialCode: '+689',  name: 'French Polynesia' },
  { isoCode: 'TF', dialCode: '+262',  name: 'French Southern Lands' },
  { isoCode: 'GA', dialCode: '+241',  name: 'Gabon' },
  { isoCode: 'GM', dialCode: '+220',  name: 'Gambia' },
  { isoCode: 'GE', dialCode: '+995',  name: 'Georgia' },
  { isoCode: 'GH', dialCode: '+233',  name: 'Ghana' },
  { isoCode: 'GI', dialCode: '+350',  name: 'Gibraltar' },
  { isoCode: 'GR', dialCode: '+30',   name: 'Greece' },
  { isoCode: 'GL', dialCode: '+299',  name: 'Greenland' },
  { isoCode: 'GD', dialCode: '+1473', name: 'Grenada' },
  { isoCode: 'GP', dialCode: '+590',  name: 'Guadeloupe' },
  { isoCode: 'GU', dialCode: '+1671', name: 'Guam' },
  { isoCode: 'GT', dialCode: '+502',  name: 'Guatemala' },
  { isoCode: 'GG', dialCode: '+44',   name: 'Guernsey' },
  { isoCode: 'GN', dialCode: '+224',  name: 'Guinea' },
  { isoCode: 'GW', dialCode: '+245',  name: 'Guinea-Bissau' },
  { isoCode: 'GY', dialCode: '+592',  name: 'Guyana' },
  { isoCode: 'HT', dialCode: '+509',  name: 'Haiti' },
  { isoCode: 'HM', dialCode: '+61',   name: 'Heard and McDonald Islands' },
  { isoCode: 'HN', dialCode: '+504',  name: 'Honduras' },
  { isoCode: 'HK', dialCode: '+852',  name: 'Hong Kong' },
  { isoCode: 'HU', dialCode: '+36',   name: 'Hungary' },
  { isoCode: 'IS', dialCode: '+354',  name: 'Iceland' },
  { isoCode: 'IN', dialCode: '+91',   name: 'India' },
  { isoCode: 'ID', dialCode: '+62',   name: 'Indonesia' },
  { isoCode: 'IR', dialCode: '+98',   name: 'Iran' },
  { isoCode: 'IQ', dialCode: '+964',  name: 'Iraq' },
  { isoCode: 'IM', dialCode: '+44',   name: 'Isle of Man' },
  { isoCode: 'IL', dialCode: '+972',  name: 'Israel' },
  { isoCode: 'IT', dialCode: '+39',   name: 'Italy' },
  { isoCode: 'JM', dialCode: '+1876', name: 'Jamaica' },
  { isoCode: 'JP', dialCode: '+81',   name: 'Japan' },
  { isoCode: 'JE', dialCode: '+44',   name: 'Jersey' },
  { isoCode: 'JO', dialCode: '+962',  name: 'Jordan' },
  { isoCode: 'KZ', dialCode: '+7',    name: 'Kazakhstan' },
  { isoCode: 'KE', dialCode: '+254',  name: 'Kenya' },
  { isoCode: 'KI', dialCode: '+686',  name: 'Kiribati' },
  { isoCode: 'KP', dialCode: '+850',  name: 'Korea, North' },
  { isoCode: 'KR', dialCode: '+82',   name: 'Korea, South' },
  { isoCode: 'KW', dialCode: '+965',  name: 'Kuwait' },
  { isoCode: 'KG', dialCode: '+996',  name: 'Kyrgyzstan' },
  { isoCode: 'LA', dialCode: '+856',  name: 'Laos' },
  { isoCode: 'LV', dialCode: '+371',  name: 'Latvia' },
  { isoCode: 'LB', dialCode: '+961',  name: 'Lebanon' },
  { isoCode: 'LS', dialCode: '+266',  name: 'Lesotho' },
  { isoCode: 'LR', dialCode: '+231',  name: 'Liberia' },
  { isoCode: 'LY', dialCode: '+218',  name: 'Libya' },
  { isoCode: 'LI', dialCode: '+423',  name: 'Liechtenstein' },
  { isoCode: 'LT', dialCode: '+370',  name: 'Lithuania' },
  { isoCode: 'LU', dialCode: '+352',  name: 'Luxembourg' },
  { isoCode: 'MO', dialCode: '+853',  name: 'Macau' },
  { isoCode: 'MK', dialCode: '+389',  name: 'Macedonia' },
  { isoCode: 'MG', dialCode: '+261',  name: 'Madagascar' },
  { isoCode: 'MW', dialCode: '+265',  name: 'Malawi' },
  { isoCode: 'MY', dialCode: '+60',   name: 'Malaysia' },
  { isoCode: 'MV', dialCode: '+960',  name: 'Maldives' },
  { isoCode: 'ML', dialCode: '+223',  name: 'Mali' },
  { isoCode: 'MT', dialCode: '+356',  name: 'Malta' },
  { isoCode: 'MH', dialCode: '+692',  name: 'Marshall Islands' },
  { isoCode: 'MQ', dialCode: '+596',  name: 'Martinique' },
  { isoCode: 'MR', dialCode: '+222',  name: 'Mauritania' },
  { isoCode: 'MU', dialCode: '+230',  name: 'Mauritius' },
  { isoCode: 'YT', dialCode: '+262',  name: 'Mayotte' },
  { isoCode: 'FM', dialCode: '+691',  name: 'Micronesia' },
  { isoCode: 'MD', dialCode: '+373',  name: 'Moldova' },
  { isoCode: 'MC', dialCode: '+377',  name: 'Monaco' },
  { isoCode: 'MN', dialCode: '+976',  name: 'Mongolia' },
  { isoCode: 'ME', dialCode: '+382',  name: 'Montenegro' },
  { isoCode: 'MS', dialCode: '+1664', name: 'Montserrat' },
  { isoCode: 'MA', dialCode: '+212',  name: 'Morocco' },
  { isoCode: 'MZ', dialCode: '+258',  name: 'Mozambique' },
  { isoCode: 'MM', dialCode: '+95',   name: 'Myanmar' },
  { isoCode: 'NA', dialCode: '+264',  name: 'Namibia' },
  { isoCode: 'NR', dialCode: '+674',  name: 'Nauru' },
  { isoCode: 'NP', dialCode: '+977',  name: 'Nepal' },
  { isoCode: 'NL', dialCode: '+31',   name: 'Netherlands' },
  { isoCode: 'NC', dialCode: '+687',  name: 'New Caledonia' },
  { isoCode: 'NI', dialCode: '+505',  name: 'Nicaragua' },
  { isoCode: 'NE', dialCode: '+227',  name: 'Niger' },
  { isoCode: 'NG', dialCode: '+234',  name: 'Nigeria' },
  { isoCode: 'NU', dialCode: '+683',  name: 'Niue' },
  { isoCode: 'NF', dialCode: '+672',  name: 'Norfolk Island' },
  { isoCode: 'MP', dialCode: '+1670', name: 'Northern Mariana Islands' },
  { isoCode: 'NO', dialCode: '+47',   name: 'Norway' },
  { isoCode: 'OM', dialCode: '+968',  name: 'Oman' },
  { isoCode: 'PK', dialCode: '+92',   name: 'Pakistan' },
  { isoCode: 'PW', dialCode: '+680',  name: 'Palau' },
  { isoCode: 'PS', dialCode: '+970',  name: 'Palestine' },
  { isoCode: 'PA', dialCode: '+507',  name: 'Panama' },
  { isoCode: 'PG', dialCode: '+675',  name: 'Papua New Guinea' },
  { isoCode: 'PY', dialCode: '+595',  name: 'Paraguay' },
  { isoCode: 'PE', dialCode: '+51',   name: 'Peru' },
  { isoCode: 'PH', dialCode: '+63',   name: 'Philippines' },
  { isoCode: 'PN', dialCode: '+64',   name: 'Pitcairn' },
  { isoCode: 'PL', dialCode: '+48',   name: 'Poland' },
  { isoCode: 'PT', dialCode: '+351',  name: 'Portugal' },
  { isoCode: 'PR', dialCode: '+1787', name: 'Puerto Rico' },
  { isoCode: 'QA', dialCode: '+974',  name: 'Qatar' },
  { isoCode: 'RE', dialCode: '+262',  name: 'Reunion' },
  { isoCode: 'RO', dialCode: '+40',   name: 'Romania' },
  { isoCode: 'RU', dialCode: '+7',    name: 'Russian Federation' },
  { isoCode: 'RW', dialCode: '+250',  name: 'Rwanda' },
  { isoCode: 'BL', dialCode: '+590',  name: 'Saint Barthélemy' },
  { isoCode: 'SH', dialCode: '+290',  name: 'Saint Helena' },
  { isoCode: 'KN', dialCode: '+1869', name: 'Saint Kitts and Nevis' },
  { isoCode: 'LC', dialCode: '+1758', name: 'Saint Lucia' },
  { isoCode: 'MF', dialCode: '+590',  name: 'Saint Martin (French part)' },
  { isoCode: 'PM', dialCode: '+508',  name: 'Saint Pierre and Miquelon' },
  { isoCode: 'VC', dialCode: '+1784', name: 'Saint Vincent and the Grenadines' },
  { isoCode: 'WS', dialCode: '+685',  name: 'Samoa' },
  { isoCode: 'SM', dialCode: '+378',  name: 'San Marino' },
  { isoCode: 'ST', dialCode: '+239',  name: 'Sao Tome and Principe' },
  { isoCode: 'SA', dialCode: '+966',  name: 'Saudi Arabia' },
  { isoCode: 'SN', dialCode: '+221',  name: 'Senegal' },
  { isoCode: 'RS', dialCode: '+381',  name: 'Serbia' },
  { isoCode: 'SC', dialCode: '+248',  name: 'Seychelles' },
  { isoCode: 'SL', dialCode: '+232',  name: 'Sierra Leone' },
  { isoCode: 'SG', dialCode: '+65',   name: 'Singapore' },
  { isoCode: 'SX', dialCode: '+1721', name: 'Sint Maarten (Dutch part)' },
  { isoCode: 'SK', dialCode: '+421',  name: 'Slovakia' },
  { isoCode: 'SI', dialCode: '+386',  name: 'Slovenia' },
  { isoCode: 'SB', dialCode: '+677',  name: 'Solomon Islands' },
  { isoCode: 'SO', dialCode: '+252',  name: 'Somalia' },
  { isoCode: 'ZA', dialCode: '+27',   name: 'South Africa' },
  { isoCode: 'GS', dialCode: '+500',  name: 'South Georgia and South Sandwich Islands' },
  { isoCode: 'SS', dialCode: '+211',  name: 'South Sudan' },
  { isoCode: 'ES', dialCode: '+34',   name: 'Spain' },
  { isoCode: 'LK', dialCode: '+94',   name: 'Sri Lanka' },
  { isoCode: 'SD', dialCode: '+249',  name: 'Sudan' },
  { isoCode: 'SR', dialCode: '+597',  name: 'Suriname' },
  { isoCode: 'SJ', dialCode: '+47',   name: 'Svalbard and Jan Mayen Islands' },
  { isoCode: 'SZ', dialCode: '+268',  name: 'Swaziland' },
  { isoCode: 'SE', dialCode: '+46',   name: 'Sweden' },
  { isoCode: 'CH', dialCode: '+41',   name: 'Switzerland' },
  { isoCode: 'SY', dialCode: '+963',  name: 'Syria' },
  { isoCode: 'TW', dialCode: '+886',  name: 'Taiwan' },
  { isoCode: 'TJ', dialCode: '+992',  name: 'Tajikistan' },
  { isoCode: 'TZ', dialCode: '+255',  name: 'Tanzania' },
  { isoCode: 'TH', dialCode: '+66',   name: 'Thailand' },
  { isoCode: 'TL', dialCode: '+670',  name: 'Timor-Leste' },
  { isoCode: 'TG', dialCode: '+228',  name: 'Togo' },
  { isoCode: 'TK', dialCode: '+690',  name: 'Tokelau' },
  { isoCode: 'TO', dialCode: '+676',  name: 'Tonga' },
  { isoCode: 'TT', dialCode: '+1868', name: 'Trinidad and Tobago' },
  { isoCode: 'TN', dialCode: '+216',  name: 'Tunisia' },
  { isoCode: 'TR', dialCode: '+90',   name: 'Turkey' },
  { isoCode: 'TM', dialCode: '+993',  name: 'Turkmenistan' },
  { isoCode: 'TC', dialCode: '+1649', name: 'Turks and Caicos Islands' },
  { isoCode: 'TV', dialCode: '+688',  name: 'Tuvalu' },
  { isoCode: 'UG', dialCode: '+256',  name: 'Uganda' },
  { isoCode: 'UA', dialCode: '+380',  name: 'Ukraine' },
  { isoCode: 'AE', dialCode: '+971',  name: 'United Arab Emirates' },
  { isoCode: 'UM', dialCode: '+1',    name: 'United States Minor Outlying Islands' },
  { isoCode: 'UY', dialCode: '+598',  name: 'Uruguay' },
  { isoCode: 'UZ', dialCode: '+998',  name: 'Uzbekistan' },
  { isoCode: 'VU', dialCode: '+678',  name: 'Vanuatu' },
  { isoCode: 'VA', dialCode: '+39',   name: 'Vatican City' },
  { isoCode: 'VE', dialCode: '+58',   name: 'Venezuela' },
  { isoCode: 'VN', dialCode: '+84',   name: 'Vietnam' },
  { isoCode: 'VG', dialCode: '+1284', name: 'Virgin Islands, British' },
  { isoCode: 'VI', dialCode: '+1340', name: 'Virgin Islands, U.S.' },
  { isoCode: 'WF', dialCode: '+681',  name: 'Wallis and Futuna Islands' },
  { isoCode: 'EH', dialCode: '+212',  name: 'Western Sahara' },
  { isoCode: 'YE', dialCode: '+967',  name: 'Yemen' },
  { isoCode: 'ZM', dialCode: '+260',  name: 'Zambia' },
  { isoCode: 'ZW', dialCode: '+263',  name: 'Zimbabwe' },
];
